import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, ButtonColor, NotificationType } from '../../components'
import env from '../../config/env'
import {
  AgeGroups,
  Brand,
  Campaign,
  CampaignAddon,
  CampaignHook,
  CampaignStatus,
  CreatorQuality,
  Ethnicity,
  ModelType,
  Platform,
  Product,
  VideoDuration,
  VideoType,
  VoiceType,
  AspectRatio,
  CreatorPetsOwner,
  CampaignVisibility,
  BrandServiceLevel,
} from '../../gql/types'
import { useUpdateCampaignMutation } from '../../hooks'
import { useCreateCampaignCheckout } from '../../hooks/payments'
import {
  ethnicityLocalizedStringInverse,
  genderLocalizedStringInverse,
} from '../../utils/GqlStrings'
import {
  PricingData,
  formatPriceInCents,
  getCampaignPricing,
  getVideoPricing,
  getPriceByQuality,
  getAddonsPricePerUnit,
} from '../../utils/PricingUtil'
import { UrlBuilder } from '../../utils/UrlBuilder'
import { capitalize, cleanUrl } from '../../utils/helpers'
import {
  isEmptyString,
  isObjectEmpty,
  useNotification,
} from '../../utils/hooks'
import {
  mapAddonToAspectRatio,
  getVideoPlacementFormValue,
  getVideoPlacementOtherValue,
} from '../../utils/mappers'
import { BrandRoutes } from '../BrandRoutes'
import {
  EditAddons,
  EditCreatorDetails,
  EditModelType,
  EditVoiceType,
  EditProductDetails,
  EditQuantity,
  EditRequirements,
  EditTitle,
  EditVariations,
  EditVideoDuration,
  EditVideoType,
  EditInspirationVideosLinks,
  EditVideoAspectRatio,
  EditCampaignVisibility,
  EditVideoPlacements,
} from './components'
import { SetCustomCreatorPrice } from '../NewCampaign/components'
import axios from 'axios'
import { useMutation } from '@apollo/client'
import { GET_REQUIREMENTS_UPLOAD_URL } from '../../gql'

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(env.STRIPE_KEY)

type EditCampaignFormData = {
  title: string
  requestedProduct: Product
  videoType: VideoType
  modelType: ModelType
  voiceType: VoiceType
  scenes: string[]
  videoDuration: VideoDuration
  platform: Platform
  modelGender: string
  ethnicity: Ethnicity
  pets: CreatorPetsOwner[]
  creatorPreferences: string
  quality: CreatorQuality
  ageGroups: (AgeGroups | 'Any')[]
  requirements: string
  countryCode: string
  hooks: string[]
  inspirationVideosLinks: string[]
  videoPlacements: string
  videoPlacementsOther: string
  addons?: CampaignAddon[] | []
  addonsSubtitlesMusic?: CampaignAddon | null
  addonsAspectRatios?: CampaignAddon[] | []
  defaultAspectRatio?: AspectRatio | null
  campaignVisibility: CampaignVisibility
  customCreatorEarningsCents: number | null
}

interface IProps {
  brand: Brand
  campaign: Campaign
  pricing: PricingData
  campaignHooks: CampaignHook[]
}

const Edit = ({ brand, campaign, pricing, campaignHooks }: IProps) => {
  const { id } = useParams()
  const brandId = parseInt(id)
  const campaignHooksDescription = campaignHooks
    ? campaignHooks.map((hook) => hook.description)
    : []
  const isEnterpriseOrWhiteGlove =
    brand?.serviceLevel === BrandServiceLevel.enterprise ||
    brand?.serviceLevel === BrandServiceLevel.whiteGlove

  const [initiateCheckoutLoading, setInitiateCheckoutLoading] = useState(false)
  const navigate = useNavigate()
  const { addNotification } = useNotification()
  const [quantity, setQuantity] = useState(3)
  const [errorsEditCampaign, setErrorsEditCampaign] = useState(null)
  const [selectedPdfFile, setSelectedPdfFile] = useState<File | null>(null)
  const [uploadingPdf, setUploadingPdf] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [savingDraft, setSavingDraft] = useState(false)

  const { createCampaignCheckout } = useCreateCampaignCheckout()
  const { updateCampaign, loading: loadingUpdateCampaign } =
    useUpdateCampaignMutation()

  const [getRequirementsUploadUrl] = useMutation(GET_REQUIREMENTS_UPLOAD_URL)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
    setError,
    setValue,
  } = useForm<EditCampaignFormData>({
    mode: 'onSubmit', // Default validation mode - less aggressive
    defaultValues: {
      title: campaign.title,
      requestedProduct: campaign.product,
      videoType: campaign.videoType,
      modelType: campaign.modelType,
      videoDuration: campaign?.videoDuration ?? VideoDuration.half,
      voiceType: campaign.voiceType,
      platform: campaign.platform,
      modelGender: campaign.modelGender
        ? capitalize(campaign.modelGender)
        : 'Any',
      ethnicity: campaign.ethnicity ? capitalize(campaign.ethnicity) : 'Any',
      pets: campaign?.pets ?? [],
      creatorPreferences: campaign.creatorPreferences,
      quality: campaign.quality ?? CreatorQuality.regular,
      ageGroups: campaign.ageGroups ?? ['Any'],
      requirements: campaign.requirements,
      countryCode: campaign.countryCode ?? brand.countryCode,
      hooks: campaignHooksDescription,
      inspirationVideosLinks: campaign?.inspirationVideosLinks ?? [],
      videoPlacements: getVideoPlacementFormValue(campaign?.videoPlacements),
      videoPlacementsOther: getVideoPlacementOtherValue(
        campaign?.videoPlacements,
      ),
      addons: campaign?.addons ?? [],
      addonsSubtitlesMusic:
        campaign?.addons?.find(
          (addon) =>
            addon === CampaignAddon.subtitles ||
            addon === CampaignAddon.subtitlesMusic,
        ) ?? null,
      addonsAspectRatios: campaign?.addons
        ? campaign?.addons?.filter(
            (addon) =>
              addon === CampaignAddon.vertical ||
              addon === CampaignAddon.square ||
              addon === CampaignAddon.portrait,
          )
        : [],
      defaultAspectRatio: campaign?.aspectRatio ?? AspectRatio.vertical,
      campaignVisibility: campaign?.visibility ?? CampaignVisibility.public,
      customCreatorEarningsCents: campaign?.customCreatorEarningsCents ?? null,
    },
  })

  // @ts-ignore
  const customCreatorEarningsCents = useWatch({
    control,
    name: 'customCreatorEarningsCents',
  }) as number | null

  // Create a helper function that hides the complex type
  const getFieldValue = (fieldName: keyof EditCampaignFormData): any => {
    // @ts-ignore - Ignoring complex union type error
    return watch(fieldName)
  }

  // @ts-ignore
  const selectedAddons = [
    // @ts-ignore
    ...(watch('addonsSubtitlesMusic') ? [watch('addonsSubtitlesMusic')] : []),
    ...watch('addonsAspectRatios'),
  ] as CampaignAddon[] | []

  // Create helper for safely getting video duration
  // Use double assertion to completely bypass type checks
  // @ts-ignore
  const selectedVideoDuration = watch('videoDuration') as VideoDuration
  const selectedQuality = watch('quality') as CreatorQuality

  // TODO: Refactor — remove this entire useEffect and replace with RHF built-in revalidation.
  // This useEffect manually reimplements what react-hook-form does automatically with its
  // default `reValidateMode: 'onChange'` setting. Each field is manually triggered and errors
  // are cleared one by one, which is error-prone (every new field must be added in two places:
  // the trigger/setError block and the dependency array).
  //
  // Recommended fix:
  // 1. Keep `mode: 'onSubmit'` (validates on submit — least aggressive, good UX).
  // 2. RHF's default `reValidateMode: 'onChange'` already clears field errors automatically
  //    once the user corrects them — no manual trigger/setError needed.
  // 3. Delete this useEffect entirely. The form-level error banner (`setErrorsEditCampaign`)
  //    can be set in the `onError` callback of `handleSubmit(onSubmit, onError)` instead.
  // 4. Apply the same refactor to NewCampaign/index.tsx (identical pattern).
  //
  // Reference: https://react-hook-form.com/docs/useform (mode / reValidateMode)
  useEffect(() => {
    if (isObjectEmpty(errors)) {
      setErrorsEditCampaign(null)
      return
    }

    if (trigger('title')) {
      setError('title', null)
    }

    if (trigger('requestedProduct')) {
      setError('requestedProduct', null)
    }

    if (trigger('videoType')) {
      setError('videoType', null)
    }

    if (trigger('modelType')) {
      setError('modelType', null)
    }

    if (trigger('voiceType')) {
      setError('voiceType', null)
    }

    if (trigger('hooks')) {
      setError('hooks', null)
    }

    if (trigger('inspirationVideosLinks')) {
      setError('inspirationVideosLinks', null)
    }

    if (trigger('videoPlacements')) {
      setError('videoPlacements', null)
    }

    if (trigger('customCreatorEarningsCents')) {
      setError('customCreatorEarningsCents', null)
    }

    if (!isObjectEmpty(errors)) {
      setErrorsEditCampaign(
        'Please check to ensure all required fields are filled out.',
      )
      return
    }

    if (isObjectEmpty(errors)) {
      setErrorsEditCampaign(null)
      return
    }
  }, [
    // @ts-ignore
    watch('title'),
    watch('requestedProduct'),
    watch('videoType'),
    watch('modelType'),
    watch('voiceType'),
    JSON.stringify(watch('hooks')),
    JSON.stringify(watch('inspirationVideosLinks')),
    watch('videoPlacements'),
    customCreatorEarningsCents,
    errors,
  ])

  // Create a helper to compute the hooks count safely
  const getHooksCount = (): number => {
    try {
      // @ts-ignore
      const hooks = watch('hooks') as any
      return hooks?.length ?? 0
    } catch (e) {
      return 0
    }
  }

  const isEnterprise = brand?.serviceLevel === BrandServiceLevel.enterprise
  const enterpriseTotalCostCents =
    isEnterprise && customCreatorEarningsCents
      ? customCreatorEarningsCents * quantity
      : 0
  const hasInsufficientCredits =
    isEnterprise &&
    enterpriseTotalCostCents > 0 &&
    enterpriseTotalCostCents > brand.creditCents

  const totalPriceCents = getCampaignPricing({
    currency: campaign?.currency,
    duration: selectedVideoDuration,
    quantity,
    quality: selectedQuality,
    // Use the helper function
    hooksCount: getHooksCount(),
    addons: selectedAddons,
    pricing,
  })

  // Create a callback function to get the selected file from EditRequirements
  const handlePdfFileChange = (file: File | null) => {
    setSelectedPdfFile(file)
  }

  // Upload PDF file
  const uploadPdfFile = async (file: File) => {
    if (!file || !campaign?.id) return null

    setUploadingPdf(true)
    setUploadProgress(0)

    try {
      const mime = file.type

      const { data } = await getRequirementsUploadUrl({
        variables: {
          campaignId: campaign.id,
          mime,
        },
      })

      const { uploadUrl, fileName } = data.getRequirementsUploadUrl

      // push content to S3 with progress tracking
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': mime,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          )
          setUploadProgress(percentCompleted)
        },
      })

      return { fileName, mime }
    } catch (error) {
      console.error('Error uploading PDF:', error)
      addNotification(
        'Failed to upload PDF. Please try again.',
        NotificationType.error,
      )
      return null
    } finally {
      setUploadingPdf(false)
    }
  }

  // API activates campaign if enough credit
  // API sends user to checkout if not enough credit
  const initiateCheckout = async () => {
    if (isEnterpriseOrWhiteGlove) {
      const isValid = await trigger('customCreatorEarningsCents')
      if (!isValid) {
        console.debug(
          '[CampaignEdit] customCreatorEarningsCents validation failed:',
          errors,
        )
        setErrorsEditCampaign(
          'Please check to ensure all required fields are filled out.',
        )
        return
      }
    }

    setInitiateCheckoutLoading(true)

    try {
      // isNavigateToCampaigns=false: manual control navigation - either to Stripe or campaigns based on payment method
      // Save campaign details without navigation redirect
      const isUpdateSuccessful = await updateCampaignDetails({
        isNavigateToCampaigns: false,
      })
      if (!isUpdateSuccessful) {
        setInitiateCheckoutLoading(false)
        return // Stop if validation failed
      }

      const { data } = await createCampaignCheckout({
        variables: {
          brandId: brand.id,
          input: {
            campaignId: campaign.id,
            quantity,
            successRedirectUrlPath: UrlBuilder.buildCampaignsUrl(brand.id),
            cancelRedirectUrlPath: UrlBuilder.buildCampaignEditUrl(
              brand.id,
              campaign.id,
            ),
          },
        },
      })

      // if not present, campaign created with credits
      if (!data.createCampaignCheckout.stripeSessionId) {
        navigate(UrlBuilder.buildCampaignsUrl(brand.id))
        addNotification('Campaign launched.', NotificationType.success)
      }

      // if stripe session id present - redirect to stripe
      if (data.createCampaignCheckout.stripeSessionId) {
        const stripe = await stripePromise

        // When the customer clicks on the button, redirect them to Checkout.
        const result = await stripe.redirectToCheckout({
          sessionId: data.createCampaignCheckout.stripeSessionId,
        })

        if (result.error) {
          // If `redirectToCheckout` fails due to a browser or network
          // error, display the localized error message to your customer
          // using `result.error.message`.
        }
      }
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }

    setInitiateCheckoutLoading(false)
  }

  const updateCampaignDetails = async (
    {
      isNavigateToCampaigns,
      isNavigateToCampaignEnabled,
    }: {
      isNavigateToCampaigns?: boolean
      isNavigateToCampaignEnabled?: boolean
    } = {
      isNavigateToCampaigns: false,
      isNavigateToCampaignEnabled: false,
    },
  ) => {
    setSavingDraft(true)
    // Call trigger with concrete field values instead of string[]
    // @ts-ignore
    const isFormValidated: boolean = await trigger([
      'title',
      'requestedProduct',
      'videoType',
      'modelType',
      'voiceType',
      'inspirationVideosLinks',
      'hooks',
      'videoPlacements',
      'videoPlacementsOther',
      ...(isEnterpriseOrWhiteGlove ? ['customCreatorEarningsCents'] : []),
    ] as const)

    if (!isFormValidated) {
      setErrorsEditCampaign(
        'Please check to ensure all required fields are filled out.',
      )
      setSavingDraft(false)
      return false
    }

    setErrorsEditCampaign(null)

    // Extract all necessary values into typed variables
    const title: string = getFieldValue('title') || ''
    const requestedProduct: any = getFieldValue('requestedProduct')

    // Use direct type assertions to bypass complex union types
    const videoType = getFieldValue('videoType') as VideoType
    const modelType = getFieldValue('modelType') as ModelType
    const voiceType = getFieldValue('voiceType') as VoiceType
    const modelGender: string = getFieldValue('modelGender') || ''
    const ethnicity: string = getFieldValue('ethnicity') || ''

    // Get pets value with direct type assertion to bypass complex union type
    const petsValue: CreatorPetsOwner[] = Array.isArray(
      getFieldValue('pets') as any,
    )
      ? (getFieldValue('pets') as any)
      : []

    const creatorPreferencesValue: string =
      getFieldValue('creatorPreferences') || ''
    const requirementsValue: string = getFieldValue('requirements') || ''
    const countryCodeValue: string = getFieldValue('countryCode') || ''

    // Get values for hooks and inspirationVideosLinks with direct type assertions
    const hooksValue: string[] = Array.isArray(getFieldValue('hooks') as any)
      ? (getFieldValue('hooks') as any)
      : []

    const inspirationVideosLinksValue: string[] = Array.isArray(
      getFieldValue('inspirationVideosLinks') as any,
    )
      ? (getFieldValue('inspirationVideosLinks') as any)
      : []

    // Use direct type assertion to bypass complex union type
    const addonsSubtitlesMusicValue: string | null = getFieldValue(
      'addonsSubtitlesMusic',
    ) as string | null

    const addonsAspectRatiosValue: any[] = Array.isArray(
      getFieldValue('addonsAspectRatios') as any,
    )
      ? (getFieldValue('addonsAspectRatios') as any)
      : []

    const defaultAspectRatioValue: AspectRatio | null =
      getFieldValue('defaultAspectRatio')

    // Process ageGroups
    let ageGroups = null
    const ageGroupsValue = getFieldValue('ageGroups') as any
    if (Array.isArray(ageGroupsValue) && !ageGroupsValue.includes('Any')) {
      ageGroups = ageGroupsValue
    }

    try {
      // Upload PDF if selected
      let fileData = null
      if (selectedPdfFile) {
        fileData = await uploadPdfFile(selectedPdfFile)
      }

      const isDraft = campaign.status === CampaignStatus.draft

      // Only send draft-only fields (hooks, addons, productId, videoDuration,
      // aspectRatio, customCreatorEarningsCents) when campaign is in draft.
      // Active/paused campaigns can only update non-structural fields.
      const input: Record<string, any> = {
        title: title,
        videoType: videoType,
        modelType: modelType,
        voiceType: voiceType,
        modelGender: genderLocalizedStringInverse(modelGender),
        ethnicity: ethnicityLocalizedStringInverse(ethnicity),
        pets: petsValue && petsValue.length > 0 ? petsValue : null,
        creatorPreferences: creatorPreferencesValue,
        quality: selectedQuality,
        ageGroups: ageGroups && ageGroups.length > 0 ? ageGroups : null,
        requirements: requirementsValue,
        requirementsName: fileData
          ? fileData.fileName
          : campaign.requirementsName,
        requirementsMime: fileData ? fileData.mime : campaign.requirementsMime,
        countryCode: countryCodeValue,
        inspirationVideosLinks: inspirationVideosLinksValue
          .filter(
            (inspirationVideosLink) => !isEmptyString(inspirationVideosLink),
          )
          .map((inspirationVideosLink) => cleanUrl(inspirationVideosLink)),
        videoPlacements:
          watch('videoPlacements') === 'other'
            ? watch('videoPlacementsOther')
            : watch('videoPlacements'),
        visibility: getFieldValue('campaignVisibility') as CampaignVisibility,
      }

      if (isDraft) {
        input.productId = requestedProduct?.id
        input.videoDuration = selectedVideoDuration
        input.hooks = hooksValue.filter((hook) => !isEmptyString(hook))
        input.addons = [
          ...(addonsSubtitlesMusicValue ? [addonsSubtitlesMusicValue] : []),
          ...addonsAspectRatiosValue,
        ] as CampaignAddon[] | []
        input.aspectRatio = defaultAspectRatioValue
        input.customCreatorEarningsCents = isEnterpriseOrWhiteGlove
          ? customCreatorEarningsCents
          : null
      }

      await updateCampaign({
        variables: {
          id: campaign.id,
          input,
        },
      })

      // Reset the PDF file state after successful update
      setSelectedPdfFile(null)

      addNotification(`Campaign details updated.`, NotificationType.success)

      if (isNavigateToCampaignEnabled) {
        navigate(
          BrandRoutes.campaignView
            .replace(':id', `${brand.id}`)
            .replace(`:campaignId`, `${campaign.id}`),
        )
      }

      // Redirect to campaigns list if not navigating to campaign
      if (isNavigateToCampaigns) {
        navigate(UrlBuilder.buildCampaignsUrl(brand.id))
      }

      return true // Return true on success
    } catch (error) {
      console.log('@error Update campaign details', error.message)
      addNotification(error.message, NotificationType.error)
    } finally {
      setSavingDraft(false)
    }
  }

  return (
    <div className='pb-12'>
      <div className='pt-6'>
        <div className='mx-auto px-4 sm:px-6 md:px-8'>
          <EditTitle
            errors={errors}
            register={register}
            watch={watch}
            campaignTitle={campaign.title}
          />

          <EditCampaignVisibility
            control={control}
            errors={errors}
            watch={watch}
          />

          {campaign.status === CampaignStatus.draft && (
            <EditProductDetails
              brand={brand}
              control={control}
              errors={errors}
            />
          )}
          <EditVideoType control={control} errors={errors} watch={watch} />
          <EditModelType control={control} errors={errors} />
          <EditVoiceType control={control} errors={errors} />
          {/* <EditPlatform control={control} errors={errors} /> */}
          {campaign.status === CampaignStatus.draft && (
            <EditVideoDuration
              control={control}
              errors={errors}
              pricing={pricing}
              currency={campaign.currency}
              quality={selectedQuality}
              hidePricing={isEnterpriseOrWhiteGlove}
            />
          )}
          <EditRequirements
            errors={errors}
            register={register}
            campaign={campaign}
            onFileChange={handlePdfFileChange}
            isUploading={uploadingPdf}
            uploadProgress={uploadProgress}
          />
          {campaign.status === CampaignStatus.draft && (
            <EditAddons
              errors={errors}
              register={register}
              control={control}
              watch={watch}
              currency={campaign.currency}
              pricing={pricing}
              hidePricing={isEnterpriseOrWhiteGlove}
            />
          )}
          {campaign.status === CampaignStatus.draft && (
            <EditVideoAspectRatio
              errors={errors}
              register={register}
              control={control}
              watch={watch}
              currency={campaign.currency}
              pricing={pricing}
              setValue={setValue}
              hidePricing={isEnterpriseOrWhiteGlove}
            />
          )}
          <EditVideoPlacements
            control={control}
            errors={errors}
            watch={watch}
            register={register}
            trigger={trigger}
          />
          {campaign.status === CampaignStatus.draft && (
            <EditVariations
              errors={errors}
              register={register}
              watch={watch}
              control={control}
              videoPricing={getVideoPricing({
                currency: campaign?.currency,
                videoDuration: selectedVideoDuration,
                pricing,
              })}
              hidePricing={isEnterpriseOrWhiteGlove}
            />
          )}
          <EditInspirationVideosLinks
            errors={errors}
            register={register}
            watch={watch}
            control={control}
          />
          <EditCreatorDetails
            control={control}
            errors={errors}
            register={register}
            watch={watch}
            campaign={campaign}
            pricing={pricing}
            setValue={setValue}
            hidePricing={isEnterpriseOrWhiteGlove}
          />
          {campaign.status === CampaignStatus.draft &&
            !isEnterpriseOrWhiteGlove && (
              <EditQuantity
                brand={brand}
                quantity={quantity}
                setQuantity={setQuantity}
                totalPriceCents={totalPriceCents}
                pricePerVideoCents={
                  getPriceByQuality(
                    selectedQuality,
                    getVideoPricing({
                      currency: campaign?.currency,
                      videoDuration: selectedVideoDuration,
                      pricing,
                    })?.price,
                  )?.priceCents
                }
                pricePerVariationCents={
                  getVideoPricing({
                    currency: campaign?.currency,
                    videoDuration: selectedVideoDuration,
                    pricing,
                  })?.hook?.priceCents
                }
                pricePerAddons={getAddonsPricePerUnit({
                  currency: campaign?.currency,
                  addons: selectedAddons,
                  duration: selectedVideoDuration,
                  pricing,
                })}
                hooksCount={getHooksCount()}
                currency={campaign.currency}
                addonsCount={selectedAddons.length}
              />
            )}
          {campaign.status === CampaignStatus.draft &&
            isEnterpriseOrWhiteGlove && (
              <SetCustomCreatorPrice
                brand={brand}
                quantity={quantity}
                setQuantity={setQuantity}
                control={control}
                errors={errors}
              />
            )}
        </div>
      </div>
      <div className='text-right px-4 sm:px-8'>
        {campaign.status === CampaignStatus.draft && hasInsufficientCredits && (
          <div className='mb-4 rounded-md bg-yellow-50 border border-yellow-200 p-4 text-left'>
            <div className='flex'>
              <div className='flex-shrink-0'>
                <svg
                  className='h-5 w-5 text-yellow-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-yellow-800'>
                  Insufficient credits
                </h3>
                <p className='mt-1 text-sm text-yellow-700'>
                  This campaign costs{' '}
                  {formatPriceInCents({
                    priceInCents: enterpriseTotalCostCents,
                    currency: brand?.currency,
                  })}{' '}
                  but you only have{' '}
                  {formatPriceInCents({
                    priceInCents: brand.creditCents,
                    currency: brand?.currency,
                  })}{' '}
                  in credits. Please contact your account manager to top up your
                  credits.
                </p>
              </div>
            </div>
          </div>
        )}
        {campaign.status === CampaignStatus.draft && (
          <>
            <Button
              title='Save Draft'
              type='button'
              colour={ButtonColor.lightGray}
              className='mr-3'
              loading={loadingUpdateCampaign || uploadingPdf || savingDraft}
              disabled={loadingUpdateCampaign || uploadingPdf || savingDraft}
              loadingPercentage={
                uploadingPdf && savingDraft ? uploadProgress : undefined
              }
              onClick={() =>
                updateCampaignDetails({ isNavigateToCampaigns: true })
              }
            />
            <Button
              title={
                isEnterpriseOrWhiteGlove ? 'Launch Campaign' : 'Complete Order'
              }
              type='button'
              loading={
                initiateCheckoutLoading ||
                (loadingUpdateCampaign && !savingDraft) ||
                (uploadingPdf && !savingDraft)
              }
              disabled={
                hasInsufficientCredits ||
                loadingUpdateCampaign ||
                initiateCheckoutLoading ||
                uploadingPdf ||
                savingDraft
              }
              loadingPercentage={
                uploadingPdf && !savingDraft ? uploadProgress : undefined
              }
              onClick={initiateCheckout}
            />
          </>
        )}
        {(campaign.status === CampaignStatus.active ||
          campaign.status === CampaignStatus.paused) && (
          <Button
            title='Update Campaign'
            type='button'
            loading={loadingUpdateCampaign || uploadingPdf}
            disabled={loadingUpdateCampaign || uploadingPdf}
            loadingPercentage={uploadingPdf ? uploadProgress : undefined}
            onClick={() =>
              updateCampaignDetails({
                isNavigateToCampaignEnabled: true,
              })
            }
          />
        )}
        {errorsEditCampaign && (
          <p className='mt-2 text-sm text-red-600'>{errorsEditCampaign}</p>
        )}
      </div>
    </div>
  )
}

export { Edit }
